
import Vue from "vue";
import MarkdownIt from "markdown-it";
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { Getter } from "vuex-class";
import { Getters } from "@/store/admin/adminGetters";
import { Benchmark, ProjectTheme, TeamEffectivenessV2ReportData, V2Project } from "@/store/admin/adminTypes";
import { SchemasCandidateAssessment } from "@/lib/serviceTypes";
import { ColorMap } from "@/lib/charts/HighChartThemes";
import { TRAITS } from "@/lib/consts";
import ComparisonRadarChartComponent, { ComparisonAssessmentItem, ComparisonBenchmarkItem, ComparisonChartPreset } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import FilterBar from "./FilterBar.vue";
import TeamPctBars from "./TeamPctBars.vue";
import TeamOrientations from "./TeamOrientations.vue";
import { avgFor, CompetencyStat, LeaderSummary, ReportFilters, Subgroup } from "./shared";

const md = new MarkdownIt({ html: true });

export const TEAM_AVERAGE_ID = "team-average";
export const SUBGROUP_AVERAGE_PREFIX = "subgroup-average:";
const TEAM_AVERAGE_COLOUR = "#027fb0"; // $blue

@Component({ components: { ComparisonRadarChartComponent, FilterBar, TeamPctBars, TeamOrientations } })
export default class TeamProfileTab extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true }) readonly reportData!: TeamEffectivenessV2ReportData;
  @Prop({ required: true }) readonly project!: V2Project;
  @Prop({ required: true }) readonly leaders!: LeaderSummary[];
  @Prop({ required: true }) readonly visibleLeaders!: LeaderSummary[];
  @Prop({ required: true }) readonly subgroups!: Subgroup[];
  @Prop({ required: true }) readonly filters!: ReportFilters;
  @Prop({ required: true }) readonly stats!: CompetencyStat[];
  @Prop({ default: false }) readonly printing!: boolean;

  @Getter(Getters.GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID, { namespace: "admin" })
  public rootBenchmarkId!: string;

  public comparisonCandidates: ComparisonAssessmentItem[] = Vue.observable([]);
  public comparisonBenchmarks: ComparisonBenchmarkItem[] = Vue.observable([]);
  public selectedPreset: ComparisonChartPreset | null = null;

  get projectTheme(): ProjectTheme {
    return (this.project?.theme as ProjectTheme) ?? ProjectTheme.DEFAULT_TA;
  }

  // Team / subgroup average benchmarks follow the current filters.
  @Watch("visibleLeaders")
  onVisibleLeadersChange() {
    this.refreshAverageSeries();
  }

  presetSelected(preset: ComparisonChartPreset) {
    this.selectedPreset = preset;
  }

  presetDeSelected() {
    this.selectedPreset = null;
  }

  renderMarkdown(text: string): string {
    return md.render(text);
  }

  private averageTraitScores(leaders: LeaderSummary[]): Record<string, number> {
    const out: Record<string, number> = {};
    TRAITS.forEach((t) => (out[t] = Math.round(avgFor(t, leaders) * 100) / 100));
    return out;
  }

  private get averagedLeaders(): LeaderSummary[] {
    return this.visibleLeaders.length ? this.visibleLeaders : this.leaders;
  }

  private isAverageId(id: string): boolean {
    return id === TEAM_AVERAGE_ID || id.startsWith(SUBGROUP_AVERAGE_PREFIX);
  }

  // Team and subgroup averages are synthetic benchmarks, so the radar sidebar labels them
  // "Benchmark" and they need no readiness literal. Their ids ("team-average",
  // "subgroup-average:{tag}") are unchanged so saved presets still find them.
  private buildAverageBenchmarks(): ComparisonBenchmarkItem[] {
    const leaders = this.averagedLeaders;
    // Team Average is plotted by default; subgroup averages start off until the user picks them.
    const items = [this.makeAverageBenchmark(TEAM_AVERAGE_ID, this.$t("Radar::TeamAverage").toString(), TEAM_AVERAGE_COLOUR, this.averageTraitScores(leaders), true)];
    this.subgroups.forEach((sg) => {
      const members = leaders.filter((l) => sg.memberIds.includes(l.id));
      if (!members.length) return;
      items.push(this.makeAverageBenchmark(`${SUBGROUP_AVERAGE_PREFIX}${sg.id}`, this.$t("Radar::SubgroupAverage", { name: sg.label }).toString(), sg.color, this.averageTraitScores(members), false));
    });
    return items;
  }

  private makeAverageBenchmark(id: string, name: string, color: string, traitScores: Record<string, number>, selectedByDefault: boolean): ComparisonBenchmarkItem {
    const existing = this.comparisonBenchmarks.find((b) => b.id === id);
    const data: Benchmark = {
      benchmarkId: id,
      name,
      colour: color,
      alwaysShow: false,
      traitScores,
      dateCreated: "",
      lastUpdated: "",
    };
    return {
      data,
      id,
      hidden: existing?.hidden ?? false,
      highlighted: false,
      locked: false,
      selected: existing?.selected ?? selectedByDefault,
      color: existing?.color ?? color,
      initialColor: color,
    } as ComparisonBenchmarkItem;
  }

  private refreshAverageSeries() {
    const projectBenchmarks = this.comparisonBenchmarks.filter((b) => !this.isAverageId(b.id));
    this.comparisonBenchmarks = Vue.observable([...this.buildAverageBenchmarks(), ...projectBenchmarks]);
  }

  mounted() {
    const projectBenchmarks =
      this.project.benchmarks?.map(
        (b) =>
          ({
            data: b as any, // eslint-disable-line @typescript-eslint/no-explicit-any
            id: b.identifier,
            hidden: false,
            highlighted: false,
            locked: false,
            selected: this.rootBenchmarkId === b.identifier || false,
            color: localStorage.getItem(`report-chartcolor-${b.identifier}`) ?? (b.colour || ColorMap[this.projectTheme].default),
            initialColor: b.colour || ColorMap[this.projectTheme].default,
          } as ComparisonBenchmarkItem),
      ) ?? [];
    this.comparisonBenchmarks = Vue.observable<ComparisonBenchmarkItem[]>([...this.buildAverageBenchmarks(), ...projectBenchmarks]);

    const individuals = this.leaders.map(
      (l) =>
        ({
          data: {
            ...(l.assessment as unknown as SchemasCandidateAssessment),
            assessmentId: l.id,
            traitScores: l.traitScores,
            readinessScore: l.readiness,
            candidateName: l.name,
            dateCompleted: l.assessment.dateCompleted ?? (l.assessment as unknown as { date_completed?: string }).date_completed,
          },
          id: l.id,
          hidden: false,
          highlighted: false,
          locked: false,
          selected: false,
          color: localStorage.getItem(`report-chartcolor-${l.id}`) ?? l.color,
          initialColor: l.color,
        } as ComparisonAssessmentItem),
    );
    this.comparisonCandidates = Vue.observable<ComparisonAssessmentItem[]>(individuals);
  }
}
